/**
 * TeamTab Component
 * Study team management - list and add form
 */

import React, { useState } from 'react';
import { MoreVertical, Mail, UserPlus } from 'lucide-react';
import type { StudyTeamMember, SiteUser, AddTeamMemberFormData } from '../types';
import { TEAM_ROLES, BLINDING_STATUSES } from '../types';
import { fieldCls } from '../../ui';

interface TeamTabProps {
  teamMembers: StudyTeamMember[];
  siteUsers: SiteUser[];
  loading: boolean;
  onAddMember: (data: AddTeamMemberFormData) => Promise<unknown>;
  onUpdateMember: (memberId: string, data: Partial<StudyTeamMember>) => Promise<unknown>;
  onRemoveMember: (memberId: string) => Promise<unknown>;
}

export function TeamTab({
  teamMembers,
  siteUsers,
  loading,
  onAddMember,
  onUpdateMember,
  onRemoveMember,
}: TeamTabProps) {
  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState<AddTeamMemberFormData>({
    user_id: '',
    role: '',
    blinding_status: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  // Filter out users who are already team members
  const existingUserIds = new Set(teamMembers.map(m => m.user_id));
  const availableUsers = siteUsers.filter(u => !existingUserIds.has(u.id));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.user_id || !formData.role) return;

    setSaving(true);
    setError(null);
    try {
      await onAddMember({
        user_id: formData.user_id,
        role: formData.role,
        blinding_status: formData.blinding_status || undefined,
      });
      setFormData({ user_id: '', role: '', blinding_status: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailTeam = () => {
    const emails = teamMembers
      .filter(m => m.status === 'active' && m.user?.email)
      .map(m => m.user?.email)
      .join(',');
    if (emails) {
      window.location.href = `mailto:${emails}`;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left: Team list */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Study Team</h3>
          <button
            onClick={handleEmailTeam}
            disabled={teamMembers.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail size={14} />
            Email Study Team
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-lg h-16 animate-pulse" />
            ))}
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <UserPlus size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No team members yet</p>
            <p className="text-sm">Add team members using the form</p>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {teamMembers.map((member) => (
                  <tr key={member.id}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.role}</div>
                      {member.blinding_status && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{member.blinding_status}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{member.user?.full_name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{member.user?.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                        member.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {member.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setMenuOpen(menuOpen === member.id ? null : member.id)}
                          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                        >
                          <MoreVertical size={16} className="text-gray-500 dark:text-gray-400" />
                        </button>
                        {menuOpen === member.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
                            <div className="absolute right-0 bottom-full mb-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 dark:bg-gray-800 dark:border-gray-700">
                              <button
                                onClick={() => {
                                  onUpdateMember(member.id, {
                                    status: member.status === 'active' ? 'inactive' : 'active',
                                  });
                                  setMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                Mark {member.status === 'active' ? 'Inactive' : 'Active'}
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm('Remove this team member?')) {
                                    onRemoveMember(member.id);
                                  }
                                  setMenuOpen(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                              >
                                Remove
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Right: Add form */}
      {showForm && (
        <div className="w-full lg:w-80 lg:flex-shrink-0">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Add Teammate</h4>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Teammate
                </label>
                <select
                  value={formData.user_id}
                  onChange={e => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                  className={`w-full ${fieldCls}`}
                  required
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className={`w-full ${fieldCls}`}
                  required
                >
                  <option value="">Select a role...</option>
                  {TEAM_ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Blinding Status (optional)
                </label>
                <select
                  value={formData.blinding_status}
                  onChange={e => setFormData(prev => ({ ...prev, blinding_status: e.target.value }))}
                  className={`w-full ${fieldCls}`}
                >
                  <option value="">Not specified</option>
                  {BLINDING_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}

              <button
                type="submit"
                disabled={saving || !formData.user_id || !formData.role}
                className="w-full inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                {saving ? 'Adding...' : 'Add Teammate'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamTab;
